import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { documents } from '@kronos/domain'
import type { Attachment, AttachmentSubject } from '@kronos/domain'
import { repository } from '../data'

/**
 * The files a part is made from: the STEP it is machined to, the Gerbers, BOM
 * and placement files a board is built from, a supplier's datasheet.
 *
 * §4.2 asks for these — "`make_external`: drawing / spec references" — and the
 * Catalog already says out loud that "the drawing is what a revision points
 * at". This is that pointer. On a *revision* rather than on a type, because
 * revisions are never edited: a part built in September can always be traced to
 * the exact file it was made from, and the stored SHA-256 is what makes that
 * checkable rather than merely claimed.
 *
 * Nothing here previews anything. A file is downloaded, never rendered, and
 * that is what lets Kronos accept whatever format a supplier asks for next
 * without an allow-list to maintain.
 */

/** Suggestions, not a list of what is allowed — the input takes any word. */
const ROLES = ['step', 'drawing', 'gerber', 'bom', 'placement', 'datasheet', 'spec']

export function fileUrl(attachmentId: string): string {
  return `/api/attachments/${encodeURIComponent(attachmentId)}/bytes`
}

/** Bytes as somebody would say them out loud. */
export function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} kB`
  const mb = kb / 1024
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`
}

export function FileList({
  subject,
  canEdit,
  canDelete = false,
  hint,
}: {
  subject: AttachmentSubject
  canEdit: boolean
  canDelete?: boolean
  hint?: string
}): JSX.Element {
  const client = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [role, setRole] = useState('')

  const key = ['attachments', subject.kind, subject.id]
  const all = useQuery({ queryKey: key, queryFn: () => repository.attachments(subject.kind, subject.id) })

  const upload = useMutation({
    mutationFn: async (files: readonly File[]) => {
      // One at a time. A Gerber set is a dozen small files and the failure that
      // matters is "the third one did not land" — which a loop reports and a
      // Promise.all hides behind whichever rejected first.
      for (const file of files) await repository.uploadDocument(subject, file, { role })
    },
    onSettled: () => client.invalidateQueries({ queryKey: key }),
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const remove = useMutation({
    mutationFn: (id: string) => repository.deleteAttachment(id),
    onSuccess: () => client.invalidateQueries({ queryKey: key }),
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  const list = documents(all.data ?? [])
  if (!canEdit && list.length === 0) return <></>

  return (
    <section className="stack" style={{ gap: 8 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 className="section-title">Files</h2>
        {canEdit ? (
          <div className="row" style={{ gap: 8 }}>
            <input
              className="role-input"
              list="attachment-roles"
              value={role}
              placeholder="what kind? (optional)"
              aria-label="What these files are"
              onChange={(e) => setRole(e.target.value.slice(0, 40))}
            />
            <datalist id="attachment-roles">
              {ROLES.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
            <input
              ref={input}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                setProblem(null)
                const files = [...(e.target.files ?? [])]
                e.target.value = ''
                if (files.length > 0) upload.mutate(files)
              }}
            />
            <button onClick={() => input.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? 'Adding…' : '+ Add files'}
            </button>
          </div>
        ) : null}
      </div>

      {problem ? <p className="critical">{problem}</p> : null}

      {list.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {hint ?? 'None yet. Whatever this is made from — a STEP file, a drawing, Gerbers, a BOM, a placement file — belongs here.'}
        </p>
      ) : (
        <ul className="file-list">
          {list.map((f) => (
            <FileRow key={f.id} file={f} canDelete={canDelete} onDelete={() => remove.mutate(f.id)} />
          ))}
        </ul>
      )}
    </section>
  )
}

function FileRow({
  file,
  canDelete,
  onDelete,
}: {
  file: Attachment
  canDelete: boolean
  onDelete: () => void
}): JSX.Element {
  return (
    <li className="file-row">
      {/* A plain link: the server sends Content-Disposition: attachment, so the
          browser downloads it under its own name rather than trying to show it. */}
      <a
        className="file-row__name serial"
        href={fileUrl(file.id)}
        download={file.filename}
        title={file.filename}
      >
        {file.filename}
      </a>
      <span className="row" style={{ gap: 8 }}>
        {file.role ? <span className="badge">{file.role}</span> : null}
        <span className="muted" style={{ fontSize: 'var(--font-size-sm)' }}>{humanSize(file.byteSize)}</span>
        {canDelete ? (
          <button className="ghost" title={`Remove ${file.filename}`} onClick={onDelete}>
            ✕
          </button>
        ) : null}
      </span>
    </li>
  )
}
